import { businessAttachmentBlocksSend } from '@/lib/business-file-attachments';
import { useApprovalPlaceholder } from './pending-approvals';
import { useProjectHomeName } from '@/workspace/projects/home';
('use client');

import { ComposerAddAttachment, ComposerAttachments } from '@/components/assistant-ui/attachment';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { Button } from '@/components/ui/button';
import { useQuestionGate } from '@/billing/context';
import { MemberLimitNotice } from '@/billing/limit-notice';
import {
  composerHighlightClass,
  findActiveComposerHighlight,
  useComposerHighlights,
} from '@/lib/composer-highlights';
import { shouldAutoFocusComposer } from '@/lib/mobile';
import { cn } from '@/lib/utils';
import { interpolateCopy, useChatCopy } from '@/lib/chat-i18n';
import { insertTranscript, type TextSelection } from '@/speech/interactive/insert';
import { ACTIVE_PHASES } from '@/speech/interactive/store';
import { DictationRow, DictationTranscript, SpeechLiveRegion } from '@/speech/ui/dictation';
import { SpeechControl } from '@/speech/ui/speech-control';
import { useSpeech, useSpeechInsertTarget, useSpeechState } from '@/speech/ui/speech-provider';
import { VoiceBar, useVoiceBarActive } from '@/voice/voice-bar';
import { VoiceEntry } from '@/voice/voice-entry';
import {
  ComposerModelPicker,
  WorkspaceComposerTools,
  useCollaborationPlaceholder,
} from '@/workspace';
import {
  AuiIf,
  ComposerPrimitive,
  useAuiEvent,
  useAuiState,
  useComposerRuntime,
} from '@assistant-ui/react';
import { ArrowUpIcon, SquareIcon } from 'lucide-react';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  type FC,
  type FormEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react';
import { PastedTextComposerContext, useLargePaste } from './pasted-text-composer';
import { ResumeCancellationContext } from '@/lib/resume-cancellation';
import { useNamespaceCopy } from '@/i18n/ns';
import steerCopy from '@/i18n/ns/steer';
import { useSteer } from '@/lib/steer/use-steer';

export const Composer: FC = () => {
  const cancelResume = useContext(ResumeCancellationContext);
  const { allow } = useQuestionGate();
  const copy = useChatCopy();
  const steer = useSteer();
  const steerText = useNamespaceCopy(steerCopy);
  const running = useAuiState((state) => state.thread.isRunning);
  const voiceActive = useVoiceBarActive();
  // While Poneva works, what the person sends goes into the work (steer, then
  // queue): the box stays open and says so.
  const steering = Boolean(steer) && running && !voiceActive;
  // A shared chat's owner is told who can read it, and a fresh fork that it
  // continues with the forker's own connections (collaboration v1), in the
  // person's language.
  const notice = useCollaborationPlaceholder();
  const approvalPlaceholder = useApprovalPlaceholder();
  // A project's home says where the chat starts: "New chat in Black Friday 2026".
  const projectHome = useProjectHomeName();
  const placeholder = notice
    ? interpolateCopy(copy[notice.key], notice.values)
    : (approvalPlaceholder ??
      (steering
        ? steerText.placeholder
        : projectHome
          ? interpolateCopy(copy.projectHomePlaceholder, { project: projectHome })
          : copy.messagePlaceholder));
  const text = useAuiState((state) => state.composer.text);
  const composer = useComposerRuntime();
  const uploadBlocked = useAuiState((state) =>
    businessAttachmentBlocksSend(state.composer.attachments),
  );
  const hasAttachments = useAuiState((state) => state.composer.attachments.length > 0);
  const speech = useSpeech();
  const speechActive = useSpeechState((state) => ACTIVE_PHASES.has(state.phase));
  const highlights = useComposerHighlights();
  const active = findActiveComposerHighlight(text, highlights);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // A run starting refocuses the composer for the person's own Send, but never takes focus from an App (a sandboxed
  // frame in the canvas) the person is using: the library default is unconditional, so it is replaced here.
  useAuiEvent('thread.runStart', () => {
    const active = document.activeElement;
    if (active instanceof HTMLIFrameElement || active?.closest('.ws-canvas')) return;
    if (!shouldAutoFocusComposer()) return;
    inputRef.current?.focus({ preventScroll: true });
  });
  const mirrorRef = useRef<HTMLDivElement>(null);
  // The caret (or selection) where committed speech goes. Kept across blur, so
  // tapping the mic or Insert does not lose it.
  const selection = useRef<TextSelection | null>(null);
  const largePaste = useLargePaste(inputRef, selection);
  const trackSelection = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget;
    selection.current = { start: selectionStart, end: selectionEnd };
  };

  // Where the caret goes once the inserted text is in the textarea. Setting
  // it before React commits the new value would leave the caret at the end,
  // so it waits for the commit (a few frames at most).
  const pendingCaret = useRef<{ text: string; caret: number } | null>(null);
  const placeCaret = (frames = 0) => {
    const pending = pendingCaret.current;
    const input = inputRef.current;
    if (!pending || !input) return;
    if (input.value !== pending.text) {
      if (frames < 10) requestAnimationFrame(() => placeCaret(frames + 1));
      return;
    }
    pendingCaret.current = null;
    input.focus({ preventScroll: true });
    input.setSelectionRange(pending.caret, pending.caret);
  };
  useLayoutEffect(() => placeCaret(), [text]);

  // Committed speech is inserted at the caret. It is never sent.
  useSpeechInsertTarget((transcript) => {
    const current = composer.getState().text;
    const at = selection.current ?? { start: current.length, end: current.length };
    const next = insertTranscript(current, at, transcript);
    pendingCaret.current = { text: next.text, caret: next.caret };
    selection.current = { start: next.caret, end: next.caret };
    composer.setText(next.text);
    placeCaret();
    return true;
  });

  // While dictating, Enter inserts (never sends) and Esc cancels the dictation.
  // Typing into the draft stays possible; the live text is read-only.
  const onInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) return;
    largePaste.onKeyDown(event);
    if (event.defaultPrevented) return;
    if (speechActive && speech && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void speech.controller.stop();
    } else if (speechActive && speech && event.key === 'Escape') {
      event.preventDefault();
      void speech.controller.cancel();
    } else if (steering && steer && event.key === 'Enter' && !event.shiftKey) {
      // Enter adds the message to the work: Poneva reads it at its next step, or
      // it goes next. Ctrl or Cmd+Enter stops Poneva and sends at once. Without
      // this the library ignores Enter while running and the box gains a line break.
      event.preventDefault();
      if (uploadBlocked || !allow()) return;
      void steer.submitComposer(event.metaKey || event.ctrlKey);
    } else if (event.key === 'Escape') {
      if (running) steer?.actions.holdForStop();
      cancelResume();
    }
  };

  const syncMirrorScroll = (scrollTop: number) => {
    if (mirrorRef.current) mirrorRef.current.style.transform = `translateY(-${scrollTop}px)`;
  };

  useEffect(() => syncMirrorScroll(inputRef.current?.scrollTop ?? 0), [text, active]);

  // Enter submits the form (ComposerInput calls requestSubmit); Root wires our
  // handler before its own send, so preventDefault keeps the signed-out draft.
  // The button has the equivalent gate because Send's click bypasses submit.
  const guardSubmit = (event: FormEvent) => {
    if (uploadBlocked || !allow()) event.preventDefault();
    else if (steering && steer) {
      event.preventDefault();
      void steer.submitComposer();
    }
  };

  return (
    <PastedTextComposerContext.Provider value={largePaste.actions}>
      <ComposerPrimitive.Root
        onSubmit={guardSubmit}
        className="aui-composer-root relative flex w-full flex-col [--composer-padding:0.25rem] [--composer-radius:1.375rem] md:[--composer-padding:8px] md:[--composer-radius:1.5rem]"
      >
        <fieldset disabled={largePaste.replaying} className="contents">
          <ComposerPrimitive.AttachmentDropzone asChild>
            <div
              data-slot="aui_composer-shell"
              className="border-border/70 data-[dragging=true]:border-ring focus-within:border-control-border/70 dark:border-muted-foreground/15 dark:focus-within:border-muted-foreground/35 flex w-full flex-col gap-1 rounded-(--composer-radius) border bg-(--composer-bg) p-(--composer-padding) shadow-(--shadow-soft) transition-[border-color,box-shadow] duration-300 ease-(--ease-out-quart) focus-within:shadow-(--shadow-lift) data-[dragging=true]:border-dashed data-[dragging=true]:bg-[color-mix(in_oklab,var(--color-accent)_50%,var(--color-background))] md:gap-2"
            >
              <WorkspaceComposerTools
                variant="chips"
                text={text}
                setText={(value) => composer.setText(value)}
                disabled={running}
              />
              {hasAttachments && (
                <div data-slot="aui_composer-attachment-tray" className="px-1 pt-1 md:px-0 md:pt-0">
                  <ComposerAttachments />
                </div>
              )}
              {/* The inspector's mention chips are portalled here: inside the card, above the
              input, where attachment chips sit (empty and invisible until there is a chip). */}
              <div
                data-slot="aui_composer-mention-chips"
                className="px-1 pt-1 empty:hidden md:px-0 md:pt-0"
              />
              {largePaste.error && (
                <p role="alert" className="text-destructive px-2 text-sm">
                  {largePaste.error}
                </p>
              )}
              <MemberLimitNotice running={running} />
              <SpeechLiveRegion />
              {/* Voice mode (BUILD 2) replaces the grid with its own bar in this
              pill; the draft stays mounted underneath, hidden. */}
              <VoiceBar />

              {/* While dictating the grid is one column: the text area (the draft,
              then the live text; over an empty draft the two share one cell,
              so the live text reads as the composer's text) above the
              recording row. */}
              <div
                data-slot="aui_composer-grid"
                data-dictating={speechActive || undefined}
                hidden={voiceActive || undefined}
                className={cn(
                  'grid w-full min-w-0 items-end gap-1',
                  speechActive
                    ? 'grid-cols-1'
                    : 'grid-cols-[auto_minmax(0,1fr)_auto] md:gap-x-0 md:gap-y-2',
                )}
              >
                {!speechActive && (
                  <div
                    data-slot="aui_composer-tools"
                    className="col-start-1 row-start-1 md:row-start-2 flex items-center"
                  >
                    <ComposerAddAttachment />
                    <WorkspaceComposerTools
                      variant="buttons"
                      text={text}
                      setText={(value) => composer.setText(value)}
                      disabled={running}
                    />
                  </div>
                )}

                <div
                  data-slot="aui_composer-input-wrap"
                  className={cn(
                    'relative min-w-0',
                    speechActive
                      ? 'grid grid-cols-1'
                      : 'col-start-2 row-start-1 md:col-span-3 md:col-start-1 md:block',
                  )}
                >
                  {active && (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 overflow-hidden"
                    >
                      <div
                        ref={mirrorRef}
                        dir="auto"
                        className="text-foreground min-h-11 whitespace-pre-wrap break-words px-1.5 py-2.5 text-base leading-6 md:min-h-10 md:px-2.5 md:py-1 md:leading-normal"
                      >
                        <span>{text.slice(0, active.start)}</span>
                        <span className={composerHighlightClass(active.highlight.tone)}>
                          {text.slice(active.start, active.end)}
                        </span>
                        <span>{text.slice(active.end)}</span>
                      </div>
                    </div>
                  )}
                  <ComposerPrimitive.Input
                    ref={inputRef}
                    placeholder={speechActive ? '' : placeholder}
                    dir="auto"
                    data-composer-highlight={active?.highlight.tone}
                    className={cn(
                      'aui-composer-input caret-brand placeholder:text-muted-foreground relative max-h-[8.5rem] min-h-11 w-full resize-none overflow-y-auto overscroll-contain bg-transparent px-1.5 py-2.5 text-base leading-6 outline-none md:max-h-32 md:min-h-10 md:px-2.5 md:py-1 md:leading-normal',
                      active &&
                        'text-transparent selection:bg-primary/20 selection:text-transparent',
                      speechActive && !text && 'z-10 [grid-area:1/1]',
                    )}
                    style={active ? { WebkitTextFillColor: 'transparent' } : undefined}
                    onPaste={largePaste.onPaste}
                    onKeyDown={onInputKeyDown}
                    onSelect={trackSelection}
                    onKeyUp={trackSelection}
                    onClick={trackSelection}
                    onBlur={trackSelection}
                    onScroll={(event) => syncMirrorScroll(event.currentTarget.scrollTop)}
                    rows={1}
                    autoFocus={shouldAutoFocusComposer()}
                    unstable_focusOnRunStart={false}
                    enterKeyHint="send"
                    aria-label={copy.messageInput}
                  />
                  {speechActive && (
                    <DictationTranscript className={text ? '' : '[grid-area:1/1]'} />
                  )}
                </div>

                {speechActive ? (
                  <DictationRow leading={<ComposerAddAttachment />} />
                ) : (
                  <ComposerEndActions />
                )}
              </div>
            </div>
          </ComposerPrimitive.AttachmentDropzone>
        </fieldset>
      </ComposerPrimitive.Root>
    </PastedTextComposerContext.Provider>
  );
};

// The send affordance. Signed-out required-auth sessions keep the draft and
// open sign-in. Both branches stay disabled while the composer is empty.
const SendButton: FC = () => {
  const { needsAuth, requestAuth, allow } = useQuestionGate();
  const copy = useChatCopy();
  const uploadBlocked = useAuiState((state) =>
    businessAttachmentBlocksSend(state.composer.attachments),
  );
  const isEmpty = useAuiState((state) => state.composer.isEmpty);
  const button = (
    <TooltipIconButton
      tooltip={copy.sendMessage}
      side="bottom"
      type="button"
      variant="default"
      size="icon"
      className="aui-composer-send size-11 rounded-full md:size-7"
      aria-label={copy.sendMessage}
      disabled={uploadBlocked || (needsAuth && isEmpty)}
      {...(needsAuth ? { onClick: requestAuth } : {})}
    >
      <ArrowUpIcon className="aui-composer-send-icon size-5 md:size-4.5" />
    </TooltipIconButton>
  );
  if (needsAuth) return button;
  return (
    <ComposerPrimitive.Send
      disabled={uploadBlocked}
      asChild
      onClick={(event) => {
        if (uploadBlocked || !allow()) event.preventDefault();
      }}
    >
      {button}
    </ComposerPrimitive.Send>
  );
};

// Not rendered while dictating: the recording row replaces it, so there is no
// path from speech to a sent turn.
const ComposerEndActions: FC = () => {
  const { allow } = useQuestionGate();
  const cancelResume = useContext(ResumeCancellationContext);
  const copy = useChatCopy();
  const steer = useSteer();
  const steerText = useNamespaceCopy(steerCopy);
  const typed = useAuiState((state) => !state.composer.isEmpty);
  const hasAttachments = useAuiState((state) => state.composer.attachments.length > 0);
  const uploadBlocked = useAuiState((state) =>
    state.composer.attachments.some(
      (a) => a.status.type === 'running' || a.status.type === 'incomplete',
    ),
  );
  // Typing while Poneva works: Send leads, and Stop steps back beside it.
  const canSteer = useAuiState((state) => state.thread.isRunning) && Boolean(steer) && typed;
  return (
    <div className="aui-composer-action-wrapper relative col-start-3 row-start-1 flex shrink-0 items-center gap-1 md:col-start-3 md:row-start-2 md:gap-1.5">
      <ComposerModelPicker />
      <SpeechControl />
      <VoiceEntry />
      <AuiIf condition={(state) => !state.thread.isRunning}>
        <SendButton />
      </AuiIf>
      <AuiIf condition={(state) => state.thread.isRunning}>
        <ComposerPrimitive.Cancel
          asChild
          onClick={() => {
            steer?.actions.holdForStop();
            cancelResume();
          }}
        >
          <Button
            type="button"
            variant={canSteer ? 'outline' : 'default'}
            size="icon"
            className={cn(
              'aui-composer-cancel size-11 rounded-full md:size-7',
              canSteer && 'bg-transparent shadow-none',
            )}
            aria-label={copy.stopGenerating}
          >
            <SquareIcon className="aui-composer-cancel-icon size-4 fill-current md:size-3.5" />
          </Button>
        </ComposerPrimitive.Cancel>
        {canSteer && steer && (
          <TooltipIconButton
            tooltip={hasAttachments ? steerText.attachmentsWait : steerText.addToWork}
            side="bottom"
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-send aui-composer-steer size-11 rounded-full md:size-7"
            aria-label={hasAttachments ? steerText.attachmentsWait : steerText.addToWork}
            aria-keyshortcuts="Enter"
            disabled={uploadBlocked}
            onClick={() => {
              if (!allow()) return;
              void steer.submitComposer();
            }}
          >
            <ArrowUpIcon className="aui-composer-send-icon size-5 md:size-4.5" />
          </TooltipIconButton>
        )}
      </AuiIf>
    </div>
  );
};
